import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useForm, SubmitHandler } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Phone, Mail, MapPin, Clock, Send, MessageCircle, Facebook, Instagram, Linkedin } from 'lucide-react'

interface FormData {
  name: string
  phone: string
  email: string
  subject: string
  message: string
}

const Contact = () => {
  const { 
    register, 
    handleSubmit, 
    reset, 
    formState: { errors } 
  } = useForm<FormData>()

  const [isSubmitting, setIsSubmitting] = useState(false)

  const contactMethods = [
    {
      icon: Phone,
      title: 'Telefone',
      info: '(47) 3017-1365',
      description: 'Atendimento de segunda a sexta, 8h às 18h',
      href: 'tel:+554730171365',
      ariaLabel: 'Ligar para (47) 3017-1365'
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      info: '(47) 99284-5121',
      description: 'Atendimento 24/7 para emergências',
      href: 'https://wa.me/5547992845121',
      ariaLabel: 'Abrir WhatsApp para (47) 99284-5121'
    },
    {
      icon: Mail,
      title: 'E-mail',
      info: 'contato@techglacial.com.br',
      description: 'Resposta em até 2 horas úteis',
      href: 'mailto:contato@techglacial.com.br',
      ariaLabel: 'Enviar email para contato@techglacial.com.br'
    },
    {
      icon: MapPin,
      title: 'Endereço',
      info: 'R: Alziro Zarur, 180',
      description: 'Bom Retiro – Joinville/SC, 89222-160',
      href: 'https://maps.google.com/?q=R.+Alziro+Zarur,+180+Bom+Retiro+Joinville+SC',
      ariaLabel: 'Ver localização no Google Maps'
    }
  ]

  const socialLinks = [
    { icon: Facebook, url: '#', name: 'Facebook', ariaLabel: 'Visite nossa página no Facebook' },
    { icon: Instagram, url: 'https://www.instagram.com/tech.glacial?igsh=czQyenJhYWFuM2Jj', name: 'Instagram', ariaLabel: 'Siga-nos no Instagram' },
    { icon: Linkedin, url: '#', name: 'LinkedIn', ariaLabel: 'Conecte-se conosco no LinkedIn' }
  ]

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    setIsSubmitting(true)

    const VITE_GOOGLE_SCRIPT_URL = import.meta.env.VITE_GOOGLE_SCRIPT_URL

    if (!VITE_GOOGLE_SCRIPT_URL) {
      console.error("A URL do Google Script não está definida no .env")
      toast.error("Ocorreu um erro na configuração. Tente novamente mais tarde.")
      setIsSubmitting(false)
      return
    }

    const sheetData = {
      Nome: data.name,
      Telefone: data.phone,
      Email: data.email,
      Assunto: data.subject,
      Mensagem: data.message || "Não informado.",
      "Data/Hora": new Date().toLocaleString("pt-BR"),
      Status: "Informações",
      Origem: "Site - Contato",
    }

    try {
      await fetch(VITE_GOOGLE_SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(sheetData),
      })

      toast.success("Solicitação enviada com sucesso! Entraremos em contato em breve.")
      reset()
    } catch (error) {
      console.error("Erro ao enviar dados para o Google Sheets:", error)
      toast.error("Houve um erro ao enviar sua solicitação. Tente novamente.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section 
        className="py-20 bg-gradient-to-r from-blue-900 to-cyan-800 text-white"
        aria-labelledby="contact-hero-title"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 id="contact-hero-title" className="text-5xl md:text-6xl font-bold mb-6">
              Entre em Contato
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto">
              Estamos prontos para atender suas necessidades. 
              Fale conosco e receba atendimento especializado.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods */}
      <section 
        className="py-16 bg-white"
        aria-labelledby="contact-methods-title"
      >
        <h2 id="contact-methods-title" className="sr-only">Formas de contato</h2>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactMethods.map((method, index) => (
              <motion.article
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow duration-300"
                aria-labelledby={`contact-method-${index}`}
              >
                <a
                  href={method.href}
                  target={method.href.startsWith('http') ? '_blank' : undefined}
                  rel={method.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  aria-label={method.ariaLabel}
                  className="block focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg"
                >
                  <div 
                    className="bg-gradient-to-r from-blue-600 to-cyan-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                    aria-hidden="true"
                  >
                    <method.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 id={`contact-method-${index}`} className="text-xl font-semibold text-gray-900 mb-2">
                    {method.title}
                  </h3>
                  <p className="text-lg text-blue-600 font-medium mb-1">
                    {method.info}
                  </p>
                  <p className="text-gray-600 text-sm">
                    {method.description}
                  </p>
                </a>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Envie sua Mensagem
              </h2>
              <p className="text-gray-600 mb-8">
                Preencha o formulário abaixo e nossa equipe entrará em contato 
                com você o mais breve possível.
              </p>

              <form 
                onSubmit={handleSubmit(onSubmit)} 
                className="space-y-6"
                aria-label="Formulário de contato"
                noValidate
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Nome Completo <span className="text-red-600" aria-label="obrigatório">*</span>
                    </label>
                    <input
                      id="name"
                      type="text"
                      {...register('name', { required: 'Nome é obrigatório' })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Seu nome completo"
                      aria-required="true"
                      aria-invalid={errors.name ? "true" : "false"}
                      aria-describedby={errors.name ? "name-error" : undefined}
                    />
                    {errors.name && (
                      <p id="name-error" className="text-red-500 text-sm mt-1" role="alert">
                        {errors.name.message as React.ReactNode}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Telefone <span className="text-red-600" aria-label="obrigatório">*</span>
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      {...register('phone', { required: 'Telefone é obrigatório' })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="(11) 99999-9999"
                      aria-required="true"
                      aria-invalid={errors.phone ? "true" : "false"}
                      aria-describedby={errors.phone ? "phone-error" : undefined}
                    />
                    {errors.phone && (
                      <p id="phone-error" className="text-red-500 text-sm mt-1" role="alert">
                        {String(errors.phone.message)}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    E-mail <span className="text-red-600" aria-label="obrigatório">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    {...register('email', { 
                      required: 'E-mail é obrigatório',
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'E-mail inválido'
                      }
                    })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="seu@email.com"
                    aria-required="true"
                    aria-invalid={errors.email ? "true" : "false"}
                    aria-describedby={errors.email ? "email-error" : undefined}
                  />
                  {errors.email && (
                    <p id="email-error" className="text-red-500 text-sm mt-1" role="alert">
                      {String(errors.email?.message)}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Assunto <span className="text-red-600" aria-label="obrigatório">*</span>
                  </label>
                  <select
                    id="subject"
                    {...register('subject', { required: 'Selecione um assunto' })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    aria-required="true"
                    aria-invalid={errors.subject ? "true" : "false"}
                    aria-describedby={errors.subject ? "subject-error" : undefined}
                    defaultValue=""
                  >
                    <option value="" disabled>Selecione o assunto</option>
                    <option value="orcamento">Solicitação de Orçamento</option>
                    <option value="manutencao">Manutenção</option>
                    <option value="emergencia">Atendimento de Emergência</option>
                    <option value="duvidas">Dúvidas Técnicas</option>
                    <option value="outros">Outros</option>
                  </select>
                  {errors.subject && (
                    <p id="subject-error" className="text-red-500 text-sm mt-1" role="alert">
                      {String(errors.subject.message)}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Mensagem <span className="text-red-600" aria-label="obrigatório">*</span>
                  </label>
                  <textarea
                    id="message"
                    {...register('message', { required: 'Mensagem é obrigatória' })}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Descreva sua necessidade ou dúvida..."
                    aria-required="true"
                    aria-invalid={errors.message ? "true" : "false"}
                    aria-describedby={errors.message ? "message-error" : undefined}
                  ></textarea>
                  {errors.message && (
                    <p id="message-error" className="text-red-500 text-sm mt-1" role="alert">
                      {String(errors.message.message)}
                    </p>
                  )}
                </div>

                <div role="status" aria-live="polite" className="sr-only">
                  {isSubmitting ? "Enviando mensagem..." : ""}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                  className={`w-full bg-gradient-to-r from-blue-600 to-cyan-500 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:from-blue-700 hover:to-cyan-600'} text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 hover:shadow-lg flex items-center justify-center space-x-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                >
                  <Send className="h-5 w-5" aria-hidden="true" />
                  <span>{isSubmitting ? "Enviando..." : "Enviar Mensagem"}</span>
                </button>
              </form>
            </motion.div>

            {/* Map & Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Nossa Localização
              </h2>
              
              {/* Map */}
              <div className="bg-gray-300 h-64 rounded-lg mb-6 overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3482.634490514792!2d-48.84689768488254!3d-26.30451598346854!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94de9b5a4f1c7b7%3A0x8e8e8e8e8e8e8e8e!2sR.%20Alziro%20Zarur%2C%20180%20-%20Bom%20Retiro%2C%20Joinville%20-%20SC%2C%2089222-160!5e0!3m2!1spt-BR!2sbr!4v1701367890123!5m2!1spt-BR!2sbr" 
                  width="100%" 
                  height="100%" 
                  style={{ border: 0 }} 
                  allowFullScreen={true}
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Mapa da localização da Tech Glacial em Joinville, SC"
                ></iframe>
              </div>

              {/* Business Hours */}
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <div className="flex items-center mb-4">
                  <Clock className="h-6 w-6 text-blue-600 mr-2" aria-hidden="true" />
                  <h3 className="text-xl font-semibold text-gray-900">
                    Horário de Funcionamento
                  </h3>
                </div>
                <dl className="space-y-2 text-gray-700">
                  <div className="flex justify-between">
                    <dt>Segunda a Sexta:</dt>
                    <dd className="font-medium">8h às 18h</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>Sábado:</dt>
                    <dd className="font-medium text-red-500">Fechado</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>Domingo:</dt>
                    <dd className="font-medium text-red-500">Fechado</dd>
                  </div>
                  <div className="border-t pt-2 mt-3">
                    <div className="flex justify-between">
                      <dt>Emergências 24/7:</dt>
                      <dd className="font-medium text-green-600">Disponível para contrato</dd>
                    </div>
                  </div>
                </dl>
              </div>

              {/* Social Links */}
              <nav aria-label="Redes sociais" className="mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Siga-nos nas Redes Sociais
                </h3>
                <div className="flex space-x-4">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.url}
                      target={social.url !== '#' ? '_blank' : undefined}
                      rel={social.url !== '#' ? 'noopener noreferrer' : undefined}
                      className="bg-gray-100 hover:bg-blue-600 text-gray-600 hover:text-white w-12 h-12 rounded-lg flex items-center justify-center transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      aria-label={social.ariaLabel}
                    >
                      <social.icon className="h-5 w-5" aria-hidden="true" />
                    </a>
                  ))}
                </div>
              </nav>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Contact